import { Component, OnInit, Renderer2, inject, ElementRef, ViewChild, DestroyRef, Output, EventEmitter, Input } from '@angular/core';
import { NgClass, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';

@Component({
  selector: 'app-volume-filter',
  imports: [NgClass, NgIf, FormsModule, MatTooltip, AssetVersionPipe],
  templateUrl: './volume-filter.component.html',
  styleUrl: './volume-filter.component.scss'
})
export class VolumeFilterComponent implements OnInit {

  private static readonly MIN_VOLUME_THRESHOLD = 500;

  @Input() variant: 'header' | 'sidebar' = 'header';
  @Output() filterApplied = new EventEmitter<number | null>();

  protected isOpen = false;
  protected inputValue: string = '';
  protected appliedValue: number | null = null;
  protected errorMessage: string = '';

  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);
  private removeGlobalListener?: () => void;
  @ViewChild('filterContainer') filterContainer!: ElementRef;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.removeGlobalListener?.());
  }

  get isActive(): boolean {
    return this.appliedValue !== null;
  }

  get isApplyDisabled(): boolean {
    return !this.inputValue || !!this.errorMessage || this.parsedValue === this.appliedValue;
  }

  get isClearDisabled(): boolean {
    return !this.isActive && !this.inputValue;
  }

  private get parsedValue(): number | null {
    const raw = this.inputValue.replace(/,/g, '');
    const num = Number(raw);
    if (!raw || isNaN(num)) return null;
    return num;
  }

  protected openFilter() {
    if (this.isOpen) {
      this.closeFilter();
      return;
    }
    if (this.appliedValue !== null) {
      this.inputValue = this.appliedValue.toLocaleString('en-US');
    }
    this.errorMessage = '';
    this.isOpen = true;
    setTimeout(() => {
      this.removeGlobalListener = this.renderer.listen(
        'document',
        'click',
        (event: Event) => {
          if (
            this.filterContainer &&
            !this.filterContainer.nativeElement.contains(event.target)
          ) {
            this.closeFilter();
          }
        }
      );
    });
  }

  protected closeFilter() {
    this.isOpen = false;
    this.removeGlobalListener?.();
    this.removeGlobalListener = undefined;
  }

  protected onInputChange() {
    this.errorMessage = '';
    const parsed = this.parsedValue;
    if (this.inputValue && parsed === null) {
      this.errorMessage = 'Enter a whole number';
      return;
    }
    if (parsed !== null && !Number.isInteger(parsed)) {
      this.errorMessage = 'Enter a whole number';
      return;
    }
    if (parsed !== null && parsed < VolumeFilterComponent.MIN_VOLUME_THRESHOLD) {
      this.errorMessage = `Minimum value is ${VolumeFilterComponent.MIN_VOLUME_THRESHOLD.toLocaleString('en-US')}`;
      return;
    }
    if (parsed !== null) {
      this.inputValue = parsed.toLocaleString('en-US');
    }
  }

  protected applyFilter() {
    if (this.isApplyDisabled) return;
    const parsed = this.parsedValue;
    this.appliedValue = parsed;
    this.filterApplied.emit(parsed);
    this.closeFilter();
  }

  protected clearFilter() {
    this.inputValue = '';
    this.errorMessage = '';
    this.appliedValue = null;
    this.filterApplied.emit(null);
    this.closeFilter();
  }

  protected onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !this.isApplyDisabled) {
      this.applyFilter();
    }
  }
}
